import React, { useEffect, useState, useRef } from "react";
import templateService from "../../../services/template.service";
import clinicService from "../../../services/clinic.service";
import Loading from "../../common/Loading";
import Swal from '../../helpers/swal';
const ClinicTemplateDashboard = () => {
    const [clinics, setClinics] = useState([]);
    const [selectedClinic, setSelectedClinic] = useState("");
    const [smsTemplates, setSmsTemplates] = useState([]);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [callTemplates, setCallTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("sms");
    const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
    const audioRef = useRef(null);
    const [audioBlobUrls, setAudioBlobUrls] = useState({}); // Store blob URLs

    useEffect(() => {
        const fetchClinics = async () => {
            try {
                setLoading(true);
                const response = await clinicService.getAllClinicsFast();
                setClinics(response.data);
            } catch (error) {
                console.error('Error fetching clinics:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load clinics. Please try again.',
                    confirmButtonColor: '#dc3545'
                });
            } finally {
                setLoading(false);
            }
        };

        fetchClinics();
    }, []);

    useEffect(() => {
        const fetchTemplates = async () => {
            if (!selectedClinic) {
                setSmsTemplates([]);
                setEmailTemplates([]);
                setCallTemplates([]);
                return;
            }

            try {
                setTemplatesLoading(true);

                // Fetch all template types in parallel
                const [smsResponse, emailResponse, callResponse] = await Promise.all([
                    templateService.getSmsTemplates(selectedClinic),
                    templateService.getEmailTemplates(selectedClinic),
                    templateService.getCallTemplates(selectedClinic)
                ]);

                setSmsTemplates(smsResponse.data);
                setEmailTemplates(emailResponse.data);
                setCallTemplates(callResponse.data);
            } catch (error) {
                console.error('Error fetching templates:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load templates. Please try again.',
                    confirmButtonColor: '#dc3545'
                });
                setSmsTemplates([]);
                setEmailTemplates([]);
                setCallTemplates([]);
            } finally {
                setTemplatesLoading(false);
            }
        };

        fetchTemplates();
    }, [selectedClinic]);

    // Clean up blob URLs when component unmounts or clinic changes
    useEffect(() => {
        return () => {
            // Revoke all blob URLs to prevent memory leaks
            Object.values(audioBlobUrls).forEach(url => {
                URL.revokeObjectURL(url);
            });
        };
    }, [audioBlobUrls]);

    const handleClinicChange = (e) => {
        setSelectedClinic(e.target.value);
        // Clean up existing blob URLs when clinic changes
        Object.values(audioBlobUrls).forEach(url => {
            URL.revokeObjectURL(url);
        });
        setAudioBlobUrls({});
    };

    const handleEditTemplate = (template, type) => {
        if (type === "sms") {
            // Navigate to SMS edit page
            window.location.href = `/clinic-templates/${selectedClinic}/edit-sms/${template.id}`;
        } else if (type === "call") {
                // Navigate to CALL edit page
                window.location.href = `/clinic-templates/${selectedClinic}/edit-call/${template.id}`;
        } else {
            console.log(`Edit ${type} template:`, template);
            Swal.fire({
                icon: 'info',
                title: `Edit ${type.toUpperCase()} Template`,
                text: `Edit functionality for template: ${template.reminderName || 'Unnamed Template'}`,
                confirmButtonColor: '#0d6efd'
            });
        }
    };

    const handlePlayAudio = async (template) => {
        try {
            // If already playing this audio, toggle pause/play
            if (currentlyPlaying === template.id) {
                if (audioRef.current.paused) {
                    audioRef.current.play();
                } else {
                    audioRef.current.pause();
                }
                return;
            }

            // Stop any currently playing audio
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
            }

            // Check if we already have a blob URL for this template
            if (audioBlobUrls[template.id]) {
                audioRef.current.src = audioBlobUrls[template.id];
                audioRef.current.play().catch(error => {
                    console.error("Error playing audio:", error);
                    Swal.fire({
                        icon: 'error',
                        title: 'Playback Error',
                        text: 'Could not play the audio file.',
                        confirmButtonColor: '#dc3545'
                    });
                });
                setCurrentlyPlaying(template.id);
                return;
            }

            // Get audio data from service - passing clinicId and templateId
            const response = await templateService.playAudio(
                selectedClinic,  // clinicId
                template.id      // templateId
            );

            // Create blob from the audio data
            const blob = new Blob([response.data], { type: 'audio/mpeg' });
            const blobUrl = URL.createObjectURL(blob);

            // Store the blob URL for future use
            setAudioBlobUrls(prev => ({
                ...prev,
                [template.id]: blobUrl
            }));

            // Set audio source and play
            audioRef.current.src = blobUrl;
            audioRef.current.play().catch(error => {
                console.error("Error playing audio:", error);
                Swal.fire({
                    icon: 'error',
                    title: 'Playback Error',
                    text: 'Could not play the audio file.',
                    confirmButtonColor: '#dc3545'
                });
                setCurrentlyPlaying(null);
            });

            setCurrentlyPlaying(template.id);

        } catch (error) {
            console.error('Error playing audio:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load audio. Please try again.',
                confirmButtonColor: '#dc3545'
            });
        }
    };

    const handleAudioEnded = () => {
        setCurrentlyPlaying(null);
    };

    if (loading) {
        return (
            <div className="container container-xxl">
                <div className="pagetitle">
                    <h1><i className="bi bi-chat-dots me-2"></i> Clinic Templates</h1>
                </div>
                <Loading />
            </div>
        );
    }

    // Helper function to get template statistics
    const getTemplateStats = (templates) => {
        return {
            total: templates.length,
            active: templates.filter(t => t.status).length,
            inactive: templates.filter(t => !t.status).length
        };
    };

    const smsStats = getTemplateStats(smsTemplates);
    const emailStats = getTemplateStats(emailTemplates);
    const callStats = getTemplateStats(callTemplates);

    // Calculate totals across all template types
    const totalTemplates = smsStats.total + emailStats.total + callStats.total;
    const totalActive = smsStats.active + emailStats.active + callStats.active;
    const totalInactive = smsStats.inactive + emailStats.inactive + callStats.inactive;

    // Get stats for current tab
    const currentStats =
        activeTab === "sms" ? smsStats :
            activeTab === "email" ? emailStats :
                callStats;

    // Get templates for current tab
    const currentTemplates =
        activeTab === "sms" ? smsTemplates :
            activeTab === "email" ? emailTemplates :
                callTemplates;

    // Get display name for template type
    const getTemplateTypeName = () => {
        switch(activeTab) {
            case "sms": return "SMS";
            case "email": return "Email";
            case "call": return "Call";
            default: return "";
        }
    };

    // Get icon for template type
    const getTemplateTypeIcon = () => {
        switch(activeTab) {
            case "sms": return "bi-chat-dots";
            case "email": return "bi-envelope";
            case "call": return "bi-telephone";
            default: return "";
        }
    };

    return (
        <div className="container container-xxl">
            {/* Hidden audio element for playback */}
            <audio
                ref={audioRef}
                onEnded={handleAudioEnded}
                onPause={() => {
                    if (audioRef.current && audioRef.current.ended) {
                        setCurrentlyPlaying(null);
                    }
                }}
            />

            <div className="pagetitle">
                <h1><i className="bi bi-chat-dots me-2"></i> Clinic Templates</h1>
            </div>

            {/* Clinic Selection Card */}
            <div className="card card-padding mb-4">
                <div className="card-title mb-0">
                    <i className="bi bi-building me-2"></i>
                    Select Clinic
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">Choose a Clinic</label>
                            <select
                                className="form-control form-select"
                                value={selectedClinic}
                                onChange={handleClinicChange}
                            >
                                <option value="">Select a clinic</option>
                                {clinics.map(clinic => (
                                    <option key={clinic.id} value={clinic.id}>
                                        {clinic.name} {clinic.city ? `- ${clinic.city}` : ''}
                                    </option>
                                ))}
                            </select>
                            <div className="form-text">
                                Select a clinic to view its templates
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="d-flex align-items-end h-100">
                                <div className="w-100">
                                    <label className="form-label fw-semibold">Template Statistics</label>
                                    <div className="d-flex gap-3">
                                        <div className="text-center p-3 border rounded bg-light w-100">
                                            <h4 className="mb-0 text-primary">{totalTemplates}</h4>
                                            <small className="text-muted">Total Templates</small>
                                        </div>
                                        <div className="text-center p-3 border rounded bg-light w-100">
                                            <h4 className="mb-0 text-success">{totalActive}</h4>
                                            <small className="text-muted">Active</small>
                                        </div>
                                        <div className="text-center p-3 border rounded bg-light w-100">
                                            <h4 className="mb-0 text-warning">{totalInactive}</h4>
                                            <small className="text-muted">Inactive</small>
                                        </div>
                                    </div>
                                    <div className="mt-2 small text-muted text-center">
                                        <span className="badge bg-info me-1">SMS: {smsStats.total}</span>
                                        <span className="badge bg-info me-1">Email: {emailStats.total}</span>
                                        <span className="badge bg-info">Call: {callStats.total}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Templates Display Card */}
            <div className="card card-padding">
                <div className="d-flex justify-content-between align-items-center">
                    <div className="card-title mb-0">
                        <i className={`bi ${getTemplateTypeIcon()} me-2`}></i>
                        {getTemplateTypeName()} Templates {selectedClinic && `- ${clinics.find(c => c.id === selectedClinic)?.name}`}
                    </div>
                    {selectedClinic && (
                        <span className="badge bg-primary">
                            {currentStats.total} {getTemplateTypeName().toLowerCase()} template(s)
                        </span>
                    )}
                </div>

                {/* Tab Navigation */}
                <div className="card-header bg-light">
                    <ul className="nav nav-tabs card-header-tabs">
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === "sms" ? "active" : ""}`}
                                onClick={() => setActiveTab("sms")}
                            >
                                <i className="bi bi-chat-dots me-1"></i> SMS Templates
                                <span className="badge bg-secondary ms-1">{smsStats.total}</span>
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === "email" ? "active" : ""}`}
                                onClick={() => setActiveTab("email")}
                            >
                                <i className="bi bi-envelope me-1"></i> Email Templates
                                <span className="badge bg-secondary ms-1">{emailStats.total}</span>
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === "call" ? "active" : ""}`}
                                onClick={() => setActiveTab("call")}
                            >
                                <i className="bi bi-telephone me-1"></i> Call Templates
                                <span className="badge bg-secondary ms-1">{callStats.total}</span>
                            </button>
                        </li>
                    </ul>
                </div>

                <div className="card-body">
                    {!selectedClinic ? (
                        <div className="text-center py-5">
                            <i className="bi bi-building display-4 text-muted"></i>
                            <h5 className="text-muted mt-3">Select a Clinic</h5>
                            <p className="text-muted">Please select a clinic from the dropdown above to view its templates.</p>
                        </div>
                    ) : templatesLoading ? (
                        <div className="text-center py-5">
                            <Loading />
                            <p className="text-muted mt-2">Loading templates...</p>
                        </div>
                    ) : currentTemplates.length === 0 ? (
                        <div className="text-center py-5">
                            <i className={`bi ${getTemplateTypeIcon()} display-4 text-muted`}></i>
                            <h5 className="text-muted mt-3">No {getTemplateTypeName()} Templates Found</h5>
                            <p className="text-muted">This clinic doesn't have any {getTemplateTypeName().toLowerCase()} templates yet.</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover table-bordered">
                                <thead className="table-light">
                                <tr>
                                    <th className="col-1 text-center">Status</th>
                                    <th className="col-2">Reminder Name</th>
                                    <th className={activeTab === "call" ? "col-4" : activeTab === "email" ? "col-5" : "col-6"}>
                                        {activeTab === "call" ? "Script Content" : "Template Content"}
                                    </th>
                                    {activeTab === "email" && <th className="col-1">Preview</th>}
                                    {activeTab === "call" && <th className="col-2">Audio</th>}
                                    <th className="col-2">Modified</th>
                                    <th className="col-1 text-center">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {currentTemplates.map(template => (
                                    <tr key={template.id}>
                                        <td className="text-center">
                                            <span className={`badge ${template.status ? 'bg-success' : 'bg-secondary'}`}>
                                                {template.status ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="fw-semibold">{template.reminderName || 'Unnamed Template'}</div>
                                            <small className="text-muted">ID: {template.id}</small>
                                        </td>
                                        <td>
                                            {activeTab === "sms" || activeTab === "call" ? (
                                                <div
                                                    className="text-break small bg-light p-2 rounded"
                                                    style={{ whiteSpace: 'pre-wrap', maxHeight: '100px', overflowY: 'auto' }}
                                                >
                                                    {template.content}
                                                </div>
                                            ) : (
                                                <div
                                                    className="text-break small bg-light p-2 rounded"
                                                    style={{ maxHeight: '100px', overflowY: 'auto' }}
                                                >
                                                    {template.content.startsWith('http') ?
                                                        'HTML template stored externally' :
                                                        template.content
                                                    }
                                                </div>
                                            )}
                                        </td>
                                        {activeTab === "email" && (
                                            <td>
                                                {template.content.startsWith('http') && (
                                                    <a
                                                        href={template.content}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-sm btn-outline-primary"
                                                    >
                                                        <i className="bi bi-eye"></i> Preview
                                                    </a>
                                                )}
                                            </td>
                                        )}
                                        {activeTab === "call" && (
                                            <td>
                                                {template.audioPath ? (
                                                    <div className="d-flex align-items-center">
                                                        <button
                                                            className={`btn btn-sm ${currentlyPlaying === template.id ? 'btn-warning' : 'btn-success'} me-2`}
                                                            onClick={() => handlePlayAudio(template)}
                                                            title={currentlyPlaying === template.id ? 'Pause Audio' : 'Play Audio'}
                                                        >
                                                            <i className={`bi ${currentlyPlaying === template.id ? 'bi-pause' : 'bi-play'}`}></i>
                                                        </button>
                                                        <div className="flex-grow-1">
                                                            <div className="progress" style={{height: '8px'}}>
                                                                <div
                                                                    className="progress-bar"
                                                                    role="progressbar"
                                                                    style={{width: currentlyPlaying === template.id ? '50%' : '0%'}}
                                                                ></div>
                                                            </div>
                                                            <small className="text-muted">Audio available</small>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span className="text-muted small">No audio</span>
                                                )}
                                            </td>
                                        )}
                                        <td>
                                            <small className="text-muted">{template.modifiedAt}</small>
                                        </td>
                                        <td className="text-center">
                                            <button
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => handleEditTemplate(template, activeTab)}
                                                title={`Edit ${getTemplateTypeName()} Template`}
                                            >
                                                <i className="bi bi-pencil"></i> Edit
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                {selectedClinic && currentStats.total > 0 && (
                    <div className="card-footer bg-light">
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">
                                Showing {currentStats.total} {getTemplateTypeName().toLowerCase()} templates
                            </span>
                            <div className="d-flex gap-2">
                                <span className="badge bg-success">
                                    {currentStats.active} active
                                </span>
                                <span className="badge bg-secondary">
                                    {currentStats.inactive} inactive
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClinicTemplateDashboard;